import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

const chapters = [
  {
    number: "I",
    title: "Trusted Expertise",
    description:
      "Licensed California CPA with a Master's in Accounting and 15+ years of experience—including 10 years in public accounting. Every engagement is led personally, never delegated.",
  },
  {
    number: "II",
    title: "Modern & Virtual",
    description:
      "A fully virtual practice built for how you actually work today. No commuting. No paper stacks. Cloud-native tools, secure document sharing, and real-time collaboration—wherever you are.",
  },
  {
    number: "III",
    title: "Proactive Strategy",
    description:
      "Compliance is the floor, not the ceiling. We anticipate what's ahead—tax law changes, growth thresholds, entity restructuring—so you're always positioned for the next move.",
  },
  {
    number: "IV",
    title: "Personalized Service",
    description:
      "Every client is unique. We take time to understand your industry, goals, and financial picture. You get individualized attention and strategy—not template advice.",
  },
];

const Chapter = ({ chapter, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 1, delay: index * 0.05, ease: [0.22, 1, 0.36, 1] }}
    className="group grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 py-12 md:py-16 border-t border-[#EBE6DD] first:border-t-0"
  >
    {/* Roman numeral */}
    <div className="md:col-span-2">
      <span className="font-heading text-6xl md:text-7xl font-light italic text-[#C85A43] leading-none block group-hover:translate-x-2 transition-transform duration-700">
        {chapter.number}
      </span>
    </div>

    {/* Title */}
    <div className="md:col-span-4">
      <h3 className="font-heading text-3xl md:text-4xl font-semibold tracking-[-0.02em] text-[#1C1917] leading-tight">
        {chapter.title}
      </h3>
    </div>

    {/* Description */}
    <div className="md:col-span-6">
      <p className="text-base md:text-lg leading-relaxed text-[#3A3634] font-medium">
        {chapter.description}
      </p>
    </div>
  </motion.div>
);

const BenefitsSection = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "-10%"]);

  return (
    <section
      ref={ref}
      id="benefits"
      className="py-24 md:py-40 bg-white relative overflow-hidden"
      data-testid="benefits-section"
    >
      <motion.div
        style={{ y: bgY }}
        className="absolute -right-40 top-40 font-heading text-[20rem] font-light italic text-[#EBE6DD]/50 leading-none pointer-events-none select-none hidden lg:block"
      >
        why
      </motion.div>

      <div className="px-6 md:px-12 lg:px-24 max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="mb-16 md:mb-24 max-w-3xl"
        >
          <div className="flex items-center gap-4 mb-6">
            <span className="h-px bg-[#C85A43] w-12" />
            <p className="text-sm md:text-base uppercase tracking-[0.3em] font-bold text-[#C85A43]">
              Chapter One · Manifesto
            </p>
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.02em] leading-[1.05] text-[#1C1917]">
            The Marapyan CPA{" "}
            <span className="italic font-light text-[#C85A43]">difference.</span>
          </h2>
        </motion.div>

        {/* Chapters */}
        <div>
          {chapters.map((chapter, i) => (
            <Chapter key={chapter.number} chapter={chapter} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default BenefitsSection;
